/**
 * Tiny UA-sniff helper for the BrowserWarningModal.
 *
 * We don't try to detect every browser perfectly — we just need to
 * answer "is the user on Chrome or Firefox?". Everyone else gets the
 * warning modal. Chromium-based browsers that don't add their own
 * marker to the UA (Brave, Arc) are treated as Chrome, which matches
 * what we'd practically support anyway.
 *
 * Detection notes:
 *   - Chrome puts `Chrome/<version>` in the UA. So do Edge (`Edg/`),
 *     Opera (`OPR/`), Samsung Internet, Yandex, Vivaldi — those all
 *     piggyback on the Chrome string. We exclude them explicitly so
 *     "Chrome" really means Chrome (or unmarked Chromium forks).
 *   - Firefox: `Firefox/<version>`. Seamonkey shares that string but
 *     marks itself, so we filter it out.
 *
 * The function is intentionally pure / SSR-safe — returns `false` when
 * navigator is unavailable so server-rendered code never spuriously
 * triggers the warning.
 */

export const isFirefox = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /Firefox\//.test(ua) && !/Seamonkey\//.test(ua);
};

export const isChrome = (): boolean => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  if (!/Chrome\//.test(ua)) return false;
  // Other Chromium browsers that DO add their own marker — these
  // should NOT count as Chrome.
  if (/Edg\//.test(ua)) return false; // Microsoft Edge
  if (/OPR\//.test(ua)) return false; // Opera
  if (/SamsungBrowser\//.test(ua)) return false; // Samsung Internet
  if (/YaBrowser\//.test(ua)) return false; // Yandex
  if (/Vivaldi\//.test(ua)) return false; // Vivaldi
  return true;
};

/** True if the user is on Chrome or Firefox — the two browsers we
 *  test extensively. Everything else triggers the warning modal. */
export const isSupportedBrowser = (): boolean => isChrome() || isFirefox();

/** Marketing URL we send users to when they pick "Get Google Chrome".
 *  Centralised here so changing it later is a one-file edit. */
export const CHROME_DOWNLOAD_URL = "https://www.google.com/chrome/";
